import { useMemo } from 'react'

import { FlattenedDocumentPage } from '@/lib/api/client'
import { useListTemplateAttributes } from '@/services/hooks/template_attributes'
import { OperationNotesEditor } from './OperationNotesEditor/OperationNotesEditor'
import { OperationLabel } from './OperationLabel'
import DraggableImage from './DraggableImage'
import { DraggableVideo } from './DraggableVideo'
import { PartsTable } from './PartsTable'
import { ToolsTable } from './ToolsTable'
import GenericTable from './GenericTable'
import { useVirtualizedElements } from './DraggableElements/useVirtualizedElements'
import { useDocumentState } from '@/state'

const DraggableElementsMap = {
  note: OperationNotesEditor,
  label: OperationLabel,
  image: DraggableImage,
  view: DraggableImage,
  video: DraggableVideo,
  'parts-table': PartsTable,
  'tools-table': ToolsTable,
  'generic-table': GenericTable,
}

const MemoComponent = ({
  Component,
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}) => {
  return useMemo(
    () => (
      <Component
        template={template}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    ),
    [
      template.id,
      template.template_values,
      dataType,
      isReadOnly,
      documentPage.id,
    ],
  )
}

type DraggableElementsProps = {
  isReadOnly: boolean
  documentPage: FlattenedDocumentPage
  annotationsContainerRef: React.RefObject<HTMLDivElement>
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const DraggableElements = ({
  isReadOnly,
  documentPage,
  annotationsContainerRef,
  onNudgeStart,
  onNudgeEnd,
}: DraggableElementsProps) => {
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage.id,
  })

  const selectedElement = useDocumentState((state) => state.selectedElement)
  const bulkSelectedTemplateAttributes = useDocumentState(
    (state) => state.bulkSelectedTemplateAttributes,
  )
  const draggingElementId = useDocumentState((state) => state.draggingElementId)

  // Build set of IDs that must stay mounted (selected, multi-selected, or being dragged)
  const forceMountedIds = useMemo(() => {
    const ids = new Set<string>()

    // Keep selected element mounted
    if (selectedElement?.id) {
      ids.add(selectedElement.id)
    }

    // Keep dragging element mounted
    if (draggingElementId) {
      ids.add(draggingElementId)
    }

    // Keep all multi-selected elements mounted
    bulkSelectedTemplateAttributes.forEach((template) => {
      if (template.id) {
        ids.add(template.id as string)
      }
    })

    return ids
  }, [selectedElement?.id, draggingElementId, bulkSelectedTemplateAttributes])

  const subPageIds = useMemo(() => {
    const subPages = documentPage?.template_values?.subPages as
      | Array<string>
      | undefined
    if (subPages) return subPages
    return documentPage?.id ? [documentPage.id] : []
  }, [documentPage])

  const draggableTemplateAttributes = useMemo(
    () =>
      templateAttributes?.filter(
        (templateAttribute) => templateAttribute.data_type !== 'annotation',
      ),
    [templateAttributes],
  )

  const { visibleElements } = useVirtualizedElements({
    templateAttributes: draggableTemplateAttributes,
    subPageIds,
    annotationsContainerRef,
    forceMountedIds,
  })

  return visibleElements?.map((templateAttribute) => {
    const dataType =
      templateAttribute.data_type !== 'table'
        ? templateAttribute.data_type
        : templateAttribute.template_values?.type

    const Component = DraggableElementsMap[dataType] || (() => <></>)

    return (
      <MemoComponent
        Component={Component}
        key={templateAttribute.id}
        template={templateAttribute}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    )
  })
}
